/**
 * GalleryListWrapper — 独立实例版本
 *
 * 每个 GalleryListWrapper 创建自有的 GalleryInstanceStore（MobX observable），
 * 通过 GalleryInstanceContext 提供给子组件，解决同页面多实例共享全局 galleryStore
 * 导致的状态冲突问题。
 *
 * 核心变更：
 * - 用 GalleryInstanceStore 替代 rootStore.galleryStore（list/total/mode/order 等状态）
 * - 子组件通过 useGalleryInstance() 获取实例级 store
 * - rootStore 仅用于访问 pagedataStore/wxuserStore/uiStore 等仍为全局单例的 store
 */
//  @ts-nocheck
import React, {
  useMemo,
  ReactNode,
  useEffect,
  useCallback,
  useState,
  useRef,
  SetStateAction,
} from 'react';
import intl from 'react-intl-universal';
import { IComponentPhoto } from 'component';
import { inject, observer } from 'mobx-react';
import { map, filter, pairwise, BehaviorSubject } from 'rxjs';

import ListWrapper, {
  checkLoadMore,
  ILoadMoreResult,
} from '../Common/ListWrapper';
import RootStore from '../../store/RootStore';
import AllForBigPic from '../BigPic/AllForBigPic';
import AllForGalleryList from './AllForGalleryList';
import AllForIndicator from '../GalleryHome/AllForIndicator';
import { maxNumberByType } from '../ComponentNavigator/jigsaw-limits';
import GalleryInstanceStore from './GalleryInstanceStore';
import { GalleryInstanceProvider } from './GalleryInstanceContext';
import { setActiveGalleryInstance, unsetActiveGalleryInstance } from './ActiveGalleryInstance';

interface IGalleryListWrapper {
  children?: ReactNode;
  component: IComponentPhoto | IComponentTagPhoto | null;
  rootStore?: RootStore;
  isContainer?: boolean;
  visible?: boolean;
}

const galleryInstanceStoreCache = new Map<string, GalleryInstanceStore>();

const getGalleryStoreCacheKey = (component: IComponentPhoto | IComponentTagPhoto | null) => {
  if (!component || !component.cid) {
    return '';
  }
  const pageLinkId = window.pageDetail && window.pageDetail.link_id
    ? String(window.pageDetail.link_id)
    : 'unknown-page';
  return `${pageLinkId}:${String(component.cid)}`;
};

//  url => url('|"....'|")
export const getUrlFromBackgroundImageUrl = (url) => {
  if (url) {
    const match = url.match(/url\((['"]?)(.*?)\1\)/);
    if (match && match[2]) {
      return match[2];
    }
  }
  return '';
};

/**
 * 这个主要是用来实现下面 scrollToListItem 内部的动画
 * @constructor
 */
export const CloseAnimation = ({
  targetElementSelectorFunction,
  containerSelectorFunction,
  list,
  index,
}) => {
  const [indexChangeSubject$] = React.useState(() => new BehaviorSubject(-1));

  useEffect(() => {
    let currentIndex = -1;

    const getCurrentIndexSubscriber = indexChangeSubject$.subscribe((index) => {
      currentIndex = index;
    });

    const subscription = indexChangeSubject$
      .pipe(
        map((index) => index > -1),
        pairwise(),
        map(([prevIndex, currentIndex]) => {
          return prevIndex === false && currentIndex === true;
        }),
        filter(Boolean)
      )
      .subscribe(() => {
        const item = list[currentIndex];
        const targetSelector = targetElementSelectorFunction(item);
        const scopeSelector = containerSelectorFunction(item);
        const scope = document.querySelector(scopeSelector);
        const originElements = Array.from(
          document.querySelectorAll(targetSelector)
        ).filter((element) => !scope || scope.contains(element));

        const lastElement = originElements[originElements.length - 1];

        if (lastElement) {
          const hasImage = lastElement.querySelector('img');

          const img = lastElement.querySelector('img') || lastElement;

          const imgRect = img.getBoundingClientRect();
          const tempElement = document.createElement('div');
          tempElement.style.position = 'fixed';
          tempElement.style.top = `${imgRect.top}px`;
          tempElement.style.left = `${imgRect.left}px`;
          tempElement.style.width = `${imgRect.width}px`;
          tempElement.style.height = `${imgRect.height}px`;
          tempElement.style.zIndex = '1000';
          tempElement.style.backgroundColor = 'black';
          tempElement.style.backgroundSize = 'contain';
          tempElement.style.backgroundRepeat = 'no-repeat';
          tempElement.style.backgroundPosition = 'center';
          if (hasImage) {
            tempElement.style.backgroundImage = `url(${img.src})`;
          } else {
            tempElement.style.backgroundImage =
              lastElement.style.backgroundImage;
          }
          document.body.appendChild(tempElement);

          tempElement.animate(
            [
              {
                top: 0,
                left: 0,
                width: '100vw',
                height: 'var(--vh)',
                // opacity: hasImage ? 1 : 0.3,
                opacity: 1,
              },
            ],
            {
              duration: 350,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            }
          ).onfinish = () => {
            tempElement.remove();
          };
        }
      });

    return () => {
      subscription.unsubscribe();
      getCurrentIndexSubscriber.unsubscribe();
    };
  }, [indexChangeSubject$, list]);

  useEffect(() => {
    indexChangeSubject$.next(index);
  }, [index]);

  const [itemBeforeClose, setItemBeforeClose] = React.useState(null);
  const container = useMemo(() => {
    if (!itemBeforeClose) {
      return null;
    }
    const selector = containerSelectorFunction(itemBeforeClose);
    return document.querySelector(selector);
  }, [itemBeforeClose]);

  const targetElement = useMemo(() => {
    if (!itemBeforeClose) {
      return null;
    }
    const selector = targetElementSelectorFunction(itemBeforeClose);
    const targetElements = Array.from(
      document.querySelectorAll(selector)
    ).filter((element) => !container || container.contains(element));
    return targetElements.length
      ? targetElements[targetElements.length - 1]
      : null;
  }, [itemBeforeClose]);

  const closeAnimation = useCallback(() => {
    if (!targetElement || !container) {
      return;
    }
    setTimeout(() => {
      // 创建一个临时的动画元素
      const tempElement = document.createElement('div');
      tempElement.style.position = 'fixed';
      tempElement.style.top = '0';
      tempElement.style.left = '0';
      tempElement.style.width = '100vw';
      tempElement.style.height = 'var(--vh)';
      tempElement.style.zIndex = '1000';
      tempElement.style.backgroundColor = 'black';
      tempElement.style.backgroundPosition = 'center';

      const targetImage = targetElement.querySelector('img');
      tempElement.style.backgroundSize = 'contain';
      tempElement.style.backgroundRepeat = 'no-repeat';

      let isGalleryFind;

      if (targetImage) {
        //  这儿是第一层的图片, 也就是gallery列表页的图片
        tempElement.style.backgroundImage = `url(${targetImage.src})`;
        isGalleryFind = true;
      } else {
        isGalleryFind = false;
        const cloneTargetElement = targetElement.cloneNode(true) as HTMLElement;
        const backgroundImage = cloneTargetElement.style.backgroundImage;

        const hash = itemBeforeClose.hash;

        //  pinch-zoom-container > img >src.includes(hash)
        const containerImageList = document.querySelectorAll(
          '.pinch-zoom-container img'
        );
        let targetImage = null;
        for (let i = 0; i < containerImageList.length; i++) {
          const img = containerImageList[i];
          if (img.src.includes(hash)) {
            targetImage = img;
            break;
          }
        }

        const img = document.createElement('img');
        img.src = getUrlFromBackgroundImageUrl(backgroundImage);
        if (targetImage) {
          img.src = targetImage.src;
        } else {
          console.log('targetImage not found');
        }
        img.style.maxWidth = '100%';
        img.style.maxHeight = '100%';
        tempElement.appendChild(img);
        tempElement.style.display = 'flex';
        tempElement.style.justifyContent = 'center';
        tempElement.style.alignItems = 'center';
      }

      document.body.appendChild(tempElement);
      const targetRect = targetElement.getBoundingClientRect();
      const animation = tempElement.animate(
        [
          {
            top: `${targetRect.top}px`,
            left: `${targetRect.left}px`,
            width: `${targetRect.width}px`,
            height: `${targetRect.height}px`,
            //  这儿这样做的目的是因为gallery列表图片和放大图片是一样的, 但是根据头像找的却不一样, 如果都设置为1, 会有突兀感, 因为图片对应不起来
            opacity: isGalleryFind ? 1 : 0.1,
          },
        ],
        {
          duration: 300,
          // easing: 'ease-in-out',
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }
      );

      animation.onfinish = () => {
        tempElement.remove();
        setItemBeforeClose(null);
      };
    }, 0);
  }, [itemBeforeClose, container, targetElement]);

  useEffect(() => {
    if (index === -1) {
      //  关闭
      if (targetElement && container) {
        closeAnimation();
      }
    } else {
      //  打开
      const item = list[index];
      if (item) {
        setItemBeforeClose(item);
      }
    }
  }, [list, container, targetElement, index]);

  useEffect(() => {
    if (index === -1 && itemBeforeClose) {
      const selector = targetElementSelectorFunction(itemBeforeClose);
      const targetElements = Array.from(
        document.querySelectorAll(selector)
      ).filter((element) => !container || container.contains(element));
      const targetElement = targetElements.length
        ? targetElements[targetElements.length - 1]
        : null;
      if (targetElement) {
        targetElement.scrollIntoView({
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [index]);

  return null;
};

const GalleryListWrapperInner = observer(function GalleryListWrapperInner(
  props: IGalleryListWrapper
) {
  const { rootStore, component, isContainer, visible, children } = props;

  const cacheKey = useMemo(() => getGalleryStoreCacheKey(component), [
    component && component.cid,
    component && component.type,
  ]);

  // ---------- 创建实例级 Store ----------
  const instanceStore = useMemo(() => {
    if (cacheKey && galleryInstanceStoreCache.has(cacheKey)) {
      return galleryInstanceStoreCache.get(cacheKey)!;
    }
    const created = new GalleryInstanceStore(rootStore!);
    if (cacheKey) {
      galleryInstanceStoreCache.set(cacheKey, created);
    }
    return created;
  }, [rootStore, cacheKey]);

  // ---------- IM 消息监听 ----------
  useEffect(() => {
    instanceStore.registerIMListeners();
    return () => {
      instanceStore.unregisterIMListeners();
    };
  }, [instanceStore]);

  // ---------- 注册组件元数据（供 reload 路由） ----------
  useEffect(() => {
    if (component) {
      instanceStore.setComponentMeta(
        component.type,
        component.gallery_list,
        component.tag,
        component.type === 'tagphoto' ? component.sort_type : undefined,
        component.type === 'tagphoto' ? component.sort_order : undefined
      );
    }
  }, [instanceStore, component]);

  // ---------- 活跃实例注册（供挂件读取） ----------
  useEffect(() => {
    if (visible !== false) {
      setActiveGalleryInstance(instanceStore);
    } else {
      unsetActiveGalleryInstance(instanceStore);
    }
    return () => {
      unsetActiveGalleryInstance(instanceStore);
    };
  }, [visible, instanceStore]);

  // ---------- 组件级状态 ----------
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [disableRefresh, setDisableRefresh] = useState(false);

  // ---------- 事件监听 ----------
  const refreshRef = useRef<(() => Promise<any>) | null>(null);

  const getCurrentDataList = useCallback(() => {
    if (instanceStore.listIsPerson) {
      return instanceStore.personList || [];
    }
    if (instanceStore.hot) {
      return instanceStore.hotList || [];
    }
    return instanceStore.list || [];
  }, [instanceStore]);

  const getPhotoReloadState = useCallback(() => {
    const isPerson = !!instanceStore.listIsPerson;
    const isHot = !!instanceStore.hot;
    return {
      tag: isPerson || isHot ? '' : instanceStore.tag,
      person: isPerson,
      hot: isHot,
    };
  }, [instanceStore]);

  const hasInitializedData = useCallback(() => {
    if (instanceStore.total >= 0) {
      return true;
    }
    return (
      (instanceStore.list && instanceStore.list.length > 0) ||
      (instanceStore.hotList && instanceStore.hotList.length > 0) ||
      (instanceStore.personList && instanceStore.personList.length > 0)
    );
  }, [instanceStore]);

  const refresh = useCallback(() => {
    return new Promise((resolve) => {
      console.log('refresh in pull');
      const container = document.querySelector(
        '.decorate-container'
      ) as HTMLDivElement | null;
      if (container) container.scrollTop = 0;

      if (!component) return resolve(undefined);
      const { type = 'photo' } = component;
      if (type === 'tagphoto' && (!component.gallery_list || !component.gallery_list.length)) {
        return resolve([]);
      }

      const result = instanceStore.reload({
        notmerge: true,
        ...(type === 'photo' ? getPhotoReloadState() : {}),
      });
      result && result.then((res: any) => resolve(res.data));
    });
  }, [instanceStore, component, getPhotoReloadState]);

  refreshRef.current = refresh;

  const loadMore = useCallback(() => {
    if (!component) {
      return Promise.resolve({ nomore: true, message: '' } as ILoadMoreResult);
    }
    const { type = 'photo' } = component;

    return new Promise<ILoadMoreResult>((resolve) => {
      if (type === 'tagphoto' && (!component.gallery_list || !component.gallery_list.length)) {
        return resolve({ nomore: true, message: '' });
      }

      // 首屏还没完成或者当前 tab 明确为空，不触发追加请求
      if (instanceStore.total < 0 || instanceStore.total === 0) {
        return resolve({ nomore: true, message: '' });
      }

      const currentList = getCurrentDataList();
      if (
        instanceStore.total !== Infinity &&
        instanceStore.total > 0 &&
        currentList.length >= instanceStore.total
      ) {
        return resolve({
          nomore: true,
          message: intl.get('This is the end', { words: 'photos' }),
        });
      }

      const result = instanceStore.reload({
        uselasttime: true,
        ...(type === 'photo' ? getPhotoReloadState() : {}),
      });
      if (!result) return resolve({ nomore: true, message: '' });
      return result.then(checkLoadMore).then(resolve);
    });
  }, [instanceStore, component, getCurrentDataList, getPhotoReloadState]);

  // ---------- window 事件 ----------
  useEffect(() => {
    const onRefresh = () => refreshRef.current?.();
    const onFreeze = () => setDisableRefresh(true);
    const onEnable = () => setDisableRefresh(false);
    window.addEventListener('photo-refresh', onRefresh, false);
    window.addEventListener('freeze-refresh', onFreeze, false);
    window.addEventListener('enable-refresh', onEnable, false);
    return () => {
      window.removeEventListener('photo-refresh', onRefresh);
      window.removeEventListener('freeze-refresh', onFreeze);
      window.removeEventListener('enable-refresh', onEnable);
    };
  }, []);

  // ---------- 首次加载 ----------
  useEffect(() => {
    if (!component) return;
    if (hasInitializedData()) return;

    const checkFirstTag = () => {
      const result = { tag: '', person: false, hot: false } as {
        [key: string]: any;
      };
      if (component.showall === false) {
        if (component.showperson === true) {
          result.person = true;
        } else if (component.showhot === true) {
          result.hot = true;
        } else {
          const galleryId = window.dataDetail
            ? window.dataDetail.id + ''
            : window.gallery.id + '';
          if (galleryId) {
            const targetGallery =
              rootStore!.pagedataStore.currentGallerys[galleryId];
            if (targetGallery && targetGallery.tags) {
              const tags = (targetGallery.tags || '').split(',');
              if (tags && tags.length) {
                result.tag = tags[0];
              }
            }
          }
        }
      }
      return result;
    };

    if (component.type == 'photo') {
      rootStore!.pagedataStore
        .getGallerys([
          window.dataDetail
            ? window.dataDetail.id + ''
            : window.gallery.id + '',
        ])
        .then(() => {
          const options = checkFirstTag();
          instanceStore.loadPic({ first: true, ...options });
        });
    }
    if (component.type == 'tagphoto') {
      // gallery_list 为空时跳过，避免发出空 galleryids 的无效请求
      if (!component.gallery_list || !component.gallery_list.length) return;
      rootStore!.pagedataStore
        .getGallerys(component.gallery_list)
        .then(() => {
          const options = checkFirstTag();
          instanceStore.loadTagPic({
            ...options,
            first: true,
            galleryids: component.gallery_list,
            tag: component.tag,
            sortType: component.sort_type,
            sortOrder: component.sort_order,
          });
        });
    }
  }, [component, instanceStore, rootStore, hasInitializedData]);

  // ---------- 辅助方法 ----------
  const getCurrentList = () => {
    return instanceStore.hot ? instanceStore.hotList : instanceStore.list;
  };

  const clickImage = useCallback(
    ({ id }: { index?: number; id: number }) => {
      const list = instanceStore.hot
        ? instanceStore.hotList
        : instanceStore.list;
      const realIndex = list.findIndex((one) => +one.id === +id);
      const { jigsaw } = instanceStore;
      const { bottomShow } = jigsaw;
      if (bottomShow) {
        if (realIndex === -1) {
          console.error('index is a must', realIndex);
          return;
        }
        const theOne = list[realIndex];
        const { jigsawList } = jigsaw;
        const cloneList = [...jigsawList];
        const theId = theOne.id;
        const jIndex = jigsawList.findIndex((one) => one.id == theId);
        if (jIndex > -1) {
          cloneList.splice(jIndex, 1);
        } else {
          const maxKey =
            jigsaw.type === 'download' ? 'MAX_DOWNLOAD_NUM' : 'MAX_JIGSAW_NUM';
          if (cloneList.length >= maxNumberByType(jigsaw.type)) {
            rootStore!.uiStore.showAlert({
              content: intl.get(maxKey, {
                num: maxNumberByType(jigsaw.type),
              }),
            });
            return;
          }
          cloneList.push(theOne);
        }
        instanceStore.setJigsaw({ jigsawList: cloneList });
      } else {
        setCurrentIndex(realIndex);
      }
    },
    [instanceStore, rootStore]
  );

  const indexChange = useCallback(
    (s: SetStateAction<number>) => {
      setCurrentIndex((prev) => {
        const nextIndex = typeof s === 'function' ? s(prev) : s;
        if (
          nextIndex === instanceStore.total &&
          instanceStore.total !== 0
        ) {
          rootStore!.uiStore.showToast({
            content: intl.get('This is the last one photo'),
            time: 1000,
          });
          return prev;
        }
        return nextIndex;
      });
    },
    [instanceStore, rootStore]
  );

  const getCurrentPayGood = () => {
    const { wxuserStore } = rootStore!;
    const { is_pay_down } = window.dataDetail
      ? window.dataDetail
      : window.gallery;
    if (Number(is_pay_down) > 0) {
      const good = wxuserStore.currentPayGood[String(is_pay_down)];
      return good ? good : undefined;
    }
    return undefined;
  };

  // ---------- 渲染 ----------
  if (!component) {
    return <>{children}</>;
  }

  const { total, jigsaw } = instanceStore;
  const realList = getCurrentList();

  const customGalleryListProps =
    component && component.type == 'tagphoto'
      ? {
          disabletag: true,
          disablesystem: true,
          showhot: false,
          showperson: false,
          showall: false,
        }
      : {};

  return (
    <GalleryInstanceProvider value={instanceStore}>
      <ListWrapper
        onRefresh={refresh}
        isContainer={isContainer}
        onLoadMore={loadMore}
        disable={currentIndex !== -1 || disableRefresh}
        scrollerChildrenSelector={`.decorate-container`}
        eventName={'gallery-list'}
        total={total}
        visible={visible}
      >
        {children}
        <AllForGalleryList
          list={realList}
          onClick={clickImage}
          component={{
            ...component,
            ...customGalleryListProps,
          }}
          total={total}
          jigsaw={jigsaw}
        />
        <AllForIndicator visible={visible} />
        <CloseAnimation
          containerSelectorFunction={() => {
            return '.decorate-container';
          }}
          targetElementSelectorFunction={(item: any) => {
            return `.img-wrapper[data-id="${item.id}"]`;
          }}
          list={realList}
          index={currentIndex}
          scrollToView
        />
        <AllForBigPic
          component={component}
          currentPropIndex={currentIndex}
          setCurrentPropIndex={indexChange}
          loadPic={instanceStore.loadPic}
          total={total}
          list={realList}
          setList={instanceStore.setList}
          currentPayGood={getCurrentPayGood()}
          currentPayedImages={rootStore!.wxuserStore.currentPayedImages}
        />
      </ListWrapper>
    </GalleryInstanceProvider>
  );
});

const GalleryListWrapper = inject('rootStore')(GalleryListWrapperInner);

export default GalleryListWrapper;
